import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildVmtRecordFromSelection } from '../../utils/vmt';
import { buildQuickActionPatch, buildUnmatchReferenceId } from '../../utils/vmtQuickActions';
import { signatureForCompare } from '../../utils/productSelectionSendDraft';

const useSendTabForm = ({
  selections,
  dealerContext,
  initialSelectionIndex,
  onSelectionChange,
  onRequestTabChange,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialSelectionIndex);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const baselineSignatureRef = useRef('');

  const currentSelection = useMemo(
    () => selections[selectedIndex] || null,
    [selections, selectedIndex]
  );

  const hydrateSelectionIntoForm = useCallback(
    (selection) => {
      if (!selection) {
        setFormValues({});
        setErrors({});
        baselineSignatureRef.current = '';
        return;
      }
      const record = buildVmtRecordFromSelection(selection, dealerContext);
      if (!record.status) {
        record.status =
          selection.productSelectionStatus ||
          selection.productSelectionStatusCd ||
          selection.status ||
          dealerContext?.status ||
          'Live';
      }
      if (!record.gmBrandCd) {
        record.gmBrandCd =
          selection.gmBrandCd ||
          selection.brandCd ||
          selection.brandCode ||
          selection.productBrandCode ||
          selection.productBrandCd ||
          dealerContext?.brandCd ||
          dealerContext?.brandCode ||
          '';
      }
      if (!record.programName) {
        record.programName = dealerContext?.programName || dealerContext?.programLabel || '';
      }
      if (!record.bac) {
        record.bac = dealerContext?.bac || '';
      }
      if (!record.imrUsage) {
        record.imrUsage = 'Y';
      }
      baselineSignatureRef.current = signatureForCompare(record);
      setFormValues(record);
      setErrors({});
    },
    [dealerContext]
  );

  useEffect(() => {
    hydrateSelectionIntoForm(currentSelection);
  }, [currentSelection, hydrateSelectionIntoForm]);

  useEffect(() => {
    if (selections.length === 0) {
      setSelectedIndex(0);
      onSelectionChange?.(0);
      return;
    }
    const boundedIndex = Math.min(
      Math.max(initialSelectionIndex, 0),
      Math.max(selections.length - 1, 0)
    );
    setSelectedIndex((prev) => {
      if (prev === boundedIndex) return prev;
      onSelectionChange?.(boundedIndex);
      return boundedIndex;
    });
  }, [initialSelectionIndex, selections.length, onSelectionChange]);

  // Ensure the first selection hydrates immediately on entry (and whenever selections load).
  useEffect(() => {
    if (!selections.length) return;
    const boundedIndex = Math.min(
      Math.max(initialSelectionIndex, 0),
      Math.max(selections.length - 1, 0)
    );
    setSelectedIndex(boundedIndex);
    const candidate = selections[boundedIndex];
    if (candidate) {
      hydrateSelectionIntoForm(candidate);
    }
  }, [selections, initialSelectionIndex, hydrateSelectionIntoForm]);

  const handleSelectionChange = useCallback(
    (value) => {
      setSelectedIndex(value);
      onSelectionChange?.(value);
      if (typeof value === 'number' && value >= 0 && value < selections.length) {
        hydrateSelectionIntoForm(selections[value]);
      }
    },
    [onSelectionChange, selections, hydrateSelectionIntoForm]
  );

  const handleFieldChange = useCallback((field, value) => {
    let nextValue = value;
    if (field === 'imrUsage' && typeof value === 'string') {
      nextValue = value.toUpperCase();
    }
    setFormValues((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  }, []);

  const handleUnmatchReferenceId = useCallback(() => {
    const uniqueValue = buildUnmatchReferenceId(dealerContext?.bac);
    handleFieldChange('referenceId', uniqueValue);
    setErrors((prev) => ({ ...prev, referenceId: undefined }));
  }, [dealerContext?.bac, handleFieldChange]);

  const handleDateChange = useCallback(
    (field, dayValue) => {
      if (!dayValue || !dayValue.isValid()) {
        handleFieldChange(field, '');
        return;
      }
      handleFieldChange(field, dayValue.format('MM/DD/YY'));
    },
    [handleFieldChange]
  );

  const applyPreset = useCallback(
    (presetKey, options = {}) => {
      const patch = buildQuickActionPatch(presetKey, {
        bac: dealerContext?.bac,
        productCd: formValues?.productCd,
        ...options,
      });
      if (!patch || Object.keys(patch).length === 0) return;
      console.info('[SendSelection] applying preset', presetKey, patch);
      setFormValues((prev) => ({
        ...prev,
        ...patch,
      }));
      setErrors((prev) => {
        const next = { ...prev };
        Object.keys(patch).forEach((field) => {
          next[field] = undefined;
        });
        return next;
      });
    },
    [dealerContext?.bac, formValues?.productCd]
  );

  const handleCancelAndUnmatch = useCallback(() => {
    applyPreset('cancelAndUnmatch');
    if (typeof onRequestTabChange === 'function') {
      onRequestTabChange(0);
    }
  }, [applyPreset, onRequestTabChange]);

  return {
    selectedIndex,
    currentSelection,
    formValues,
    setFormValues,
    errors,
    setErrors,
    baselineSignatureRef,
    hydrateSelectionIntoForm,
    handleSelectionChange,
    handleFieldChange,
    handleDateChange,
    handleUnmatchReferenceId,
    applyPreset,
    handleCancelAndUnmatch,
  };
};

export default useSendTabForm;
